import { PersonMe } from '@/domains/-session/models/PersonMe';
import { WorkspacePersonMe } from '@/domains/-session/models/WorkspacePersonMe.ts';
import { Workspace } from '@/domains/-workspace/models/Workspace.ts';
import { getUserDefaultLang, setLocales } from '@/i18n';
import { router } from '@/router';
import { Lang, sdk } from '@/sdk';
import { AxiosError } from 'axios';
import { reactive } from 'vue';

type ContractError = AxiosError<{
  detail: {
    key: 'legal.contract.unsigned';
    contract_name: string;
    contract_id: number;
    language: string;
  };
}> & {
  response: {
    data: {
      detail: {
        key: 'legal.contract.unsigned';
        contract_name: string;
        contract_id: number;
        language: string;
      };
    };
  };
};

type AuthenticationError = AxiosError<{
  detail: 'auth.unauthenticated';
}>;

function isContractError(error: AxiosError): error is ContractError {
  return (
    'response' in error &&
    typeof error.response === 'object' &&
    error.response !== null &&
    'data' in error.response &&
    typeof error.response.data === 'object' &&
    error.response.data !== null &&
    'detail' in error.response.data &&
    typeof error.response.data.detail === 'object' &&
    error.response.data.detail !== null &&
    'key' in error.response.data.detail &&
    error.response.data.detail.key === 'legal.contract.unsigned'
  );
}

function isAuthenticationError(error: AxiosError): error is AuthenticationError {
  return (
    'response' in error &&
    typeof error.response === 'object' &&
    error.response !== null &&
    'data' in error.response &&
    typeof error.response.data === 'object' &&
    error.response.data !== null &&
    'detail' in error.response.data &&
    error.response.data.detail === 'auth.unauthenticated'
  );
}

async function handleContractError(error: ContractError): Promise<void> {
  if (error.response.data.detail.contract_name.match(/^hub_cgu/g)) {
    await router.push({
      name: `tos`,
      query: {
        contract_id: error.response.data.detail.contract_id,
        contract_name: error.response.data.detail.contract_name,
        language: error.response.data.detail.language,
      },
    });
  } else if (error.response.data.detail.contract_name.match(/^hub_legal/g)) {
    await router.push({
      name: `legal`,
      query: {
        contract_id: error.response.data.detail.contract_id,
        contract_name: error.response.data.detail.contract_name,
        language: error.response.data.detail.language,
      },
    });
  }
}

export class SessionStore {
  public loaded: boolean;
  public me: undefined | PersonMe;
  public users: WorkspacePersonMe[];
  public workspaces: Workspace[];
  public wsp: undefined | WorkspacePersonMe;
  private promise: undefined | Promise<void>;
  private _lang: undefined | Lang;

  constructor() {
    this.me = undefined;
    this.wsp = undefined;
    this.users = [];
    this.workspaces = [];
    this.loaded = false;
    this.promise = undefined;
  }

  get isAuthenticated(): boolean {
    return this.me !== undefined && this.loaded;
  }

  async loadUser(forceReload: boolean = false): Promise<void> {
    if (this.loaded && !forceReload) return;
    if (this.promise) return this.promise;

    this.promise = (async () => {
      try {
        const meResponse = await sdk.getMe();
        const person = new PersonMe(meResponse.data);
        this.me = person;
        this._lang = person.lang;
        setLocales(this.lang);

        this.loaded = true;
      } catch (error) {
        this.me = undefined;
        this.users = [];
        this.workspaces = [];

        if (isContractError(error)) return await handleContractError(error);

        if (isAuthenticationError(error)) {
          this.unload();
          return; // We don't redirect as this is supposed to happen only in `router.ts`.
        }

        this.unload();
        throw error;
      } finally {
        this.promise = undefined;
      }
    })();
    return this.promise;
  }

  async signOut() {
    //await sdk.signOut();
    this.unload();
  }

  unload() {
    this.me = undefined;
    this.users = [];
    this.workspaces = [];
    this.loaded = false;
  }

  set lang(lang: Lang) {
    this._lang = lang
  }

  get lang(): Lang {
    return this._lang ?? getUserDefaultLang();
  }
}

let sessionStore: SessionStore;

export function useSessionStore(): SessionStore {
  if (!sessionStore) sessionStore = reactive(new SessionStore()) as SessionStore;

  return sessionStore;
}
